import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, OnInit, Inject, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms'; // Used for Reactive Forms

import { Form } from '../../models/form-builder/form.model';
import { FormField } from '../../models/form-builder/form-field.model';
import { FieldDefinition } from '../../models/form-builder/field-definition.model';
import { FieldDefinitionService } from '../../services/field-definition.service';

interface IFieldsByNameMap {
    [fieldName: string]: FormField;
}

class FormFieldWithNameFormControls {
    // Properties.
    private nameFormControl: UntypedFormControl = null;
    private displayNameFormControl: UntypedFormControl = null;

    // Constructor.
    public constructor(public hostDialogue: NameFormFieldsDialog, public formField: FormField) {
        this.formField.transientNameUsedWhileEditingName = this.formField.name;

        this.nameFormControl = new UntypedFormControl({ value: formField.transientNameUsedWhileEditingName, disabled: false });
        this.displayNameFormControl = new UntypedFormControl({ value: formField.displayName, disabled: false });

        this.nameFormControl.valueChanges.subscribe(value => {
            this.hostDialogue.fieldNameChanged(this.formField, value);
        });
        this.displayNameFormControl.valueChanges.subscribe(value => {
            this.hostDialogue.fieldDisplayNameChanged(this.formField, value);
        });
    }

    // Getter methods.
    public get NameFormControl(): UntypedFormControl {
        return this.nameFormControl;
    }
    public get DisplayNameFormControl(): UntypedFormControl {
        return this.displayNameFormControl;
    }
}

@Component({
    selector: 'app-name-form-fields',
    templateUrl: './name-form-fields.dialog.html',
    styleUrls: ['./name-form-fields.dialog.scss'],
    standalone: false
})
export class NameFormFieldsDialog implements OnInit {
    // Properties.
    private form: Form = null;

    private formFieldsWithNameControls: FormFieldWithNameFormControls[] = [];
    private reasonSaveButtonDisabled: string = null;

    // Constructor.
    public constructor(@Inject(MAT_DIALOG_DATA) data, // An instance of Form
        private dialogRef: MatDialogRef<NameFormFieldsDialog>,
        private fieldDefinitionService: FieldDefinitionService)
    {
        this.form = <Form>data;
    }

    // Life cycle methods.
    public ngOnInit(): void {
        // Wrap form fields.
        if (this.form.formFields != null) {            
            for (let index: number = 0; index < this.form.formFields.length; index++) {
                let formField: FormField = this.form.formFields[index];
                let formFieldWithNameControls: FormFieldWithNameFormControls = new FormFieldWithNameFormControls(this, formField);

                this.formFieldsWithNameControls.push(formFieldWithNameControls);
            }
        }
    }

    // Define data accessor methods called by my HTML code.
    public get AllFormFields(): FormFieldWithNameFormControls[] {
        //return this.form.formFields;
        return this.formFieldsWithNameControls;
    }

    public getFieldTypeName(formField: FormField): string {
        let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(formField.fieldDefinitionClassName);
        return fieldDef.displayName;
    }

    public get SaveButtonDisabled(): boolean {
        let disabled: boolean = false;

        let fieldsByNameMap: IFieldsByNameMap = {};
        let reasonSaveButtonDisabled: string = null;

        if (this.form.formFields != null) {
            for (let index: number = 0; index < this.form.formFields.length; index++) {
                let formField: FormField = this.form.formFields[index];

                if ((formField.transientNameUsedWhileEditingName == null) || (formField.transientNameUsedWhileEditingName.trim() == '')) {
                    disabled = true;
                    reasonSaveButtonDisabled = `A field internal name cannot be blank`;
                } else if (this.containsEmbeddedWhitespace(formField.transientNameUsedWhileEditingName.trim())) {
                    disabled = true;
                    reasonSaveButtonDisabled = `Field internal name '${formField.transientNameUsedWhileEditingName.trim()}' cannot contain spaces`;
                } else if (fieldsByNameMap[formField.name.trim()] != null) {
                    disabled = true;
                    reasonSaveButtonDisabled = `More than one field uses internal name '${formField.name.trim() }'`;
                } else
                    fieldsByNameMap[formField.name.trim()] = formField;

                if (disabled)
                    break;
            }

            if (reasonSaveButtonDisabled != this.reasonSaveButtonDisabled) {
                // Use a timeout to avoid a 'ExpressionChangedAfterItHasBeenCheckedError' error.
                setTimeout(() => {
                    this.reasonSaveButtonDisabled = reasonSaveButtonDisabled;
                }, 0);
            }
        }

        return disabled;
    }

    public get ReasonSaveButtonDisabled(): string {
        return this.reasonSaveButtonDisabled;
    }

    // Handle control events.
    public fieldNameChanged(formField: FormField, value: string): void {
        formField.transientNameUsedWhileEditingName = value;
    }

    public fieldDisplayNameChanged(formField: FormField, value: string): void {
        formField.displayName = value;
    }

    public saveClicked(): void {
        // Update names within the form's layout.
        for (let index: number = 0; index < this.formFieldsWithNameControls.length; index++) {
            let formFieldWithNameControls: FormFieldWithNameFormControls = this.formFieldsWithNameControls[index];

            if (formFieldWithNameControls.formField.transientNameUsedWhileEditingName != formFieldWithNameControls.formField.name) {
                // We need to update this field's name within the form layout.
                this.form.formLayout.fieldNameUpdated(formFieldWithNameControls.formField.name, formFieldWithNameControls.formField.id, formFieldWithNameControls.formField.transientNameUsedWhileEditingName);
                formFieldWithNameControls.formField.name = formFieldWithNameControls.formField.transientNameUsedWhileEditingName;
            }
        }

        // Return the form as the dialogue's result to indicate that a save should be performed.
        this.dialogRef.close(this.form);
    }

    public cancelClicked(): void {
        this.dialogRef.close(null);
    }

    // Helper methods.
    private containsEmbeddedWhitespace(str: string): boolean {
        let hasEmbeddedSpace: boolean = /\s/.test(str);
        return hasEmbeddedSpace;
    } 
}
